'use client'

/**
 * Shared Circle upcoming state for Home + Play (Phase 3.2I).
 * One fetch, one dogId, one refresh — both surfaces read the same items.
 *
 * Lifecycle guards (honor-system v1):
 * - Optimistic accept/complete update items immediately.
 * - Refresh generation tokens drop stale responses.
 * - Completed tombstones block agreed resurrection.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { CirclePlaydateDto } from '@/lib/play/circlePlaydateDto'
import {
  indexOpenCirclePlaydatesByRelationship,
  loadCircleUpcomingPlaydates,
  reconcileUpcomingCirclePlaydates,
  removeCompletedCirclePlaydateFromItems,
  selectAgreedCirclePlaydates,
  selectEarliestAgreedCirclePlaydate,
  selectEarliestFutureAgreedCirclePlaydate,
  shouldLoadCircleUpcoming,
  upsertAgreedCirclePlaydateInItems,
} from '@/lib/play/circleUpcomingSource'
import { formatCirclePlaydateWhenLabel } from '@/lib/play/formatCirclePlaydateWhen'

export type CircleUpcomingAgreedPreview = {
  playdateId: string
  relationshipId: string
  friendDogId: string
  locationLabel: string
  whenLabel: string
  eyebrow: 'Upcoming playdate'
}

export type CircleUpcomingController = {
  items: CirclePlaydateDto[]
  openByRelationship: Record<string, CirclePlaydateDto>
  agreed: CirclePlaydateDto[]
  earliestAgreed: CirclePlaydateDto | null
  agreedPreview: CircleUpcomingAgreedPreview | null
  loading: boolean
  enabled: boolean
  dogId: string | null
  refresh: (opts?: { retain?: CirclePlaydateDto | null }) => Promise<void>
  /** Immediate agreed upsert — do not wait for network. */
  applyOptimisticAgreed: (playdate: CirclePlaydateDto) => void
  /** Immediate removal + tombstone — do not wait for network. */
  applyOptimisticCompleted: (input: {
    playdateId: string
    relationshipId: string
  }) => void
}

const CircleUpcomingContext = createContext<CircleUpcomingController | null>(
  null
)

function friendDogId(playdate: CirclePlaydateDto): string {
  return playdate.viewerRole === 'initiator'
    ? playdate.inviteeDog.id
    : playdate.initiatorDog.id
}

function toAgreedPreview(
  playdate: CirclePlaydateDto,
  now: Date = new Date()
): CircleUpcomingAgreedPreview {
  const whenLabel = formatCirclePlaydateWhenLabel(
    {
      scheduledStartAt: playdate.scheduledStartAt,
      timezone: playdate.timezone,
      presentationMode: playdate.presentationMode,
    },
    now
  )
  const locationLabel =
    playdate.parkName || playdate.customLocationLabel || 'Public place'
  return {
    playdateId: playdate.id,
    relationshipId: playdate.relationshipId,
    friendDogId: friendDogId(playdate),
    locationLabel,
    whenLabel,
    eyebrow: 'Upcoming playdate',
  }
}

export function useCircleUpcomingState(input: {
  dogId: string | null
  circleEnabled: boolean
  playdatesEnabled: boolean
  authenticated: boolean
}): CircleUpcomingController {
  const enabled = shouldLoadCircleUpcoming({
    circleEnabled: input.circleEnabled,
    playdatesEnabled: input.playdatesEnabled,
    authenticated: input.authenticated,
    dogId: input.dogId,
  })
  const dogId = enabled ? (input.dogId as string) : null

  const [items, setItems] = useState<CirclePlaydateDto[]>([])
  const [loading, setLoading] = useState(false)
  const itemsRef = useRef(items)
  itemsRef.current = items
  const refreshGenerationRef = useRef(0)
  const completedPlaydateIdsRef = useRef<Set<string>>(new Set())

  const applyOptimisticAgreed = useCallback((playdate: CirclePlaydateDto) => {
    if (playdate.status !== 'agreed') return
    completedPlaydateIdsRef.current.delete(playdate.id)
    // Invalidate in-flight refreshes so they cannot overwrite with proposed.
    refreshGenerationRef.current += 1
    setItems((prev) => upsertAgreedCirclePlaydateInItems(prev, playdate))
  }, [])

  const applyOptimisticCompleted = useCallback(
    (inputComplete: { playdateId: string; relationshipId: string }) => {
      completedPlaydateIdsRef.current.add(inputComplete.playdateId)
      refreshGenerationRef.current += 1
      setItems((prev) =>
        removeCompletedCirclePlaydateFromItems(
          prev,
          inputComplete.playdateId,
          inputComplete.relationshipId
        )
      )
    },
    []
  )

  const refresh = useCallback(
    async (opts?: { retain?: CirclePlaydateDto | null }) => {
      if (!enabled || !dogId) {
        setItems([])
        return
      }
      const generation = ++refreshGenerationRef.current
      setLoading(true)
      try {
        const result = await loadCircleUpcomingPlaydates(dogId)
        if (generation !== refreshGenerationRef.current) return

        const nextItems = reconcileUpcomingCirclePlaydates({
          fetched: result.parsedOk ? result.items : [],
          previous: itemsRef.current,
          completedPlaydateIds: completedPlaydateIdsRef.current,
          retain: opts?.retain,
          activeDogId: dogId,
        })
        if (generation !== refreshGenerationRef.current) return
        setItems(nextItems)
      } catch {
        if (generation !== refreshGenerationRef.current) return
        if (opts?.retain) {
          setItems((prev) =>
            reconcileUpcomingCirclePlaydates({
              fetched: prev,
              previous: prev,
              completedPlaydateIds: completedPlaydateIdsRef.current,
              retain: opts.retain,
              activeDogId: dogId,
            })
          )
        }
      } finally {
        if (generation === refreshGenerationRef.current) {
          setLoading(false)
        }
      }
    },
    [dogId, enabled]
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    const onFocus = () => {
      void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, refresh])

  return useMemo(() => {
    const agreed = selectAgreedCirclePlaydates(items)
    const earliestAgreed = selectEarliestAgreedCirclePlaydate(items)
    // Home "Upcoming" prefers a future confirmed playdate; overdue is fallback
    // so We Met remains reachable when nothing future is scheduled.
    const upcomingAgreed =
      selectEarliestFutureAgreedCirclePlaydate(items) ?? earliestAgreed
    return {
      items,
      openByRelationship: indexOpenCirclePlaydatesByRelationship(items),
      agreed,
      earliestAgreed,
      agreedPreview: upcomingAgreed ? toAgreedPreview(upcomingAgreed) : null,
      loading,
      enabled,
      dogId,
      refresh,
      applyOptimisticAgreed,
      applyOptimisticCompleted,
    }
  }, [
    applyOptimisticAgreed,
    applyOptimisticCompleted,
    dogId,
    enabled,
    items,
    loading,
    refresh,
  ])
}

export function CircleUpcomingProvider({
  value,
  children,
}: {
  value: CircleUpcomingController
  children: React.ReactNode
}) {
  return (
    <CircleUpcomingContext.Provider value={value}>
      {children}
    </CircleUpcomingContext.Provider>
  )
}

export function useCircleUpcoming(): CircleUpcomingController | null {
  return useContext(CircleUpcomingContext)
}
